import { DateTime } from 'luxon';
import { OrderType } from '../../quipli-service/order/order';
import { ExcludeProductFeeRequest } from '../../quipli-service/financials/financials';
import { CustomerProduct } from '../product/customer-product';

export class Cart {
  readonly id!: string;
  readonly userId?: number;
  readonly sessionId!: string;
  readonly items!: CartItem[];
  readonly meta?: {
    readonly coupon?: string;
    readonly excludeFeeIds?: number[];
    readonly excludeProductFees?: ExcludeProductFeeRequest[];
    readonly excludeDamageWaiver?: boolean;
    readonly selectedAddresses?: {
      billing?: number;
      shipping?: number;
    };
  };
}

export abstract class CartItem {
  readonly id!: string;
  readonly cartId!: string;
  readonly type!: OrderType;
  readonly product!: CustomerProduct;
  readonly quantity!: number;
  readonly options!: Record<string, string>;

  protected constructor(
    id: string,
    cartId: string,
    type: OrderType,
    product: CustomerProduct,
    quantity: number,
    options: Record<string, string>,
  ) {
    this.id = id;
    this.cartId = cartId;
    this.type = type;
    this.product = product;
    this.quantity = quantity;
    this.options = options;
  }
}

export class RentalCartItem extends CartItem {
  readonly startDate!: DateTime;
  readonly endDate!: DateTime;

  constructor(
    id: string,
    cartId: string,
    product: CustomerProduct,
    quantity: number,
    options: Record<string, string>,
    startDateTime: DateTime,
    endDateTime: DateTime,
  ) {
    super(id, cartId, OrderType.Rental, product, quantity, options);
    this.startDate = startDateTime;
    this.endDate = endDateTime;
  }
}

export class Service {
  id!: number;
  value?: string;
}

export class SaleCartItem extends CartItem {
  readonly services!: Service[];

  constructor(
    id: string,
    cartId: string,
    product: CustomerProduct,
    quantity: number,
    options: Record<string, string>,
    services: Service[],
  ) {
    super(id, cartId, OrderType.Sale, product, quantity, options);
    this.services = services;
  }
}

export abstract class AddCartItemRequest {
  readonly type!: OrderType;
  readonly productId!: number;
  readonly quantity!: number;
  readonly shippingType?: string;
  readonly options!: Record<string, string>;
  readonly skipAvailabilityCheck!: boolean;

  protected constructor(
    type: OrderType,
    productId: number,
    quantity: number,
    shippingType: string | undefined,
    options: Record<string, string> | undefined,
    skipAvailabilityCheck: boolean | undefined,
  ) {
    this.type = type;
    this.productId = productId;
    this.quantity = quantity;
    this.shippingType = shippingType;
    this.options = options || {};
    this.skipAvailabilityCheck = !!skipAvailabilityCheck;
  }
}

export class AddRentalCartItemRequest extends AddCartItemRequest {
  readonly startDate!: DateTime;
  readonly endDate!: DateTime;

  constructor(
    productId: number,
    quantity: number,
    shippingType: string | undefined,
    options: Record<string, string> | undefined,
    startDateTime: DateTime,
    endDateTime: DateTime,
    skipAvailabilityCheck: boolean | undefined,
  ) {
    super(OrderType.Rental, productId, quantity, shippingType, options, skipAvailabilityCheck);
    this.startDate = startDateTime;
    this.endDate = endDateTime;
  }
}

export class AddSaleCartItemRequest extends AddCartItemRequest {
  readonly services!: Service[];
  constructor(
    productId: number,
    quantity: number,
    shippingType: string | undefined,
    options: Record<string, string> | undefined,
    services: Service[] | undefined,
    skipAvailabilityCheck: boolean | undefined,
  ) {
    super(OrderType.Sale, productId, quantity, shippingType, options, skipAvailabilityCheck);
    this.services = services || [];
  }
}

export abstract class UpDateTimeCartItemRequest {
  readonly quantity!: number;
  readonly shippingType?: string;
  readonly options?: Record<string, string>;
  readonly skipAvailabilityCheck!: boolean;

  protected constructor(
    quantity: number,
    shippingType: string | undefined,
    options: Record<string, string> | undefined,
    skipAvailabilityCheck: boolean | undefined,
  ) {
    this.quantity = quantity;
    this.shippingType = shippingType;
    this.options = options;
    this.skipAvailabilityCheck = !!skipAvailabilityCheck;
  }
}

export class UpDateTimeRentalCartItemRequest extends UpDateTimeCartItemRequest {
  readonly startDateTime!: DateTime;
  readonly endDateTime!: DateTime;

  constructor(
    quantity: number,
    shippingType: string | undefined,
    options: Record<string, string> | undefined,
    startDateTime: DateTime,
    endDateTime: DateTime,
    skipAvailabilityCheck: boolean | undefined,
  ) {
    super(quantity, shippingType, options, skipAvailabilityCheck);
    this.startDateTime = startDateTime;
    this.endDateTime = endDateTime;
  }
}

export class UpDateTimeSaleCartItemRequest extends UpDateTimeCartItemRequest {
  readonly services: Service[] | undefined;
  constructor(
    quantity: number,
    shippingType: string | undefined,
    options: Record<string, string> | undefined,
    services: Service[] | undefined,
    skipAvailabilityCheck: boolean | undefined,
  ) {
    super(quantity, shippingType, options, skipAvailabilityCheck);
    this.services = services;
  }
}
